import {useEffect, useState} from "react";
import {useSelector} from "react-redux";

import {selectAvmApiFullUrl} from "../redux/envSlice";

/**
 * Jahr des Datenstands aus dem AVM-Release-Kennzeichen (`rt` aus `/v1/status`), z. B. `as_avm_20260817_q25` -> 2026.
 * @return Jahr oder null, wenn `rt` fehlt oder nicht parsbar ist
 */
export function parseBasemapDataYear(rt: unknown): number | null {
    if (typeof rt !== "string") return null;
    const match = /^as_avm_(\d{4})\d{4}/.exec(rt);
    return match ? Number(match[1]) : null;
}

// Pro AVM-URL einmal pro Sitzung geladen (BKG-Quellenvermerk basemap.de, #19302).
const yearPromises = new Map<string, Promise<number>>();
const resolvedYears = new Map<string, number>();

/** Lädt das Datenstand-Jahr; bei jedem Fehler das aktuelle Jahr. Fehlschläge werden nicht gecacht. */
export function loadBasemapDataYear(avmApiFullUrl: string): Promise<number> {
    let promise = yearPromises.get(avmApiFullUrl);
    if (!promise) {
        promise = fetch(`${avmApiFullUrl}/v1/status`)
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((status) => {
                const year = parseBasemapDataYear(status?.rt);
                if (year == null) throw new Error("rt nicht parsbar");
                resolvedYears.set(avmApiFullUrl, year);
                return year;
            })
            .catch(() => {
                yearPromises.delete(avmApiFullUrl);
                return new Date().getFullYear();
            });
        yearPromises.set(avmApiFullUrl, promise);
    }
    return promise;
}

/** Nur für Tests. */
export function resetBasemapDataYearCache(): void {
    yearPromises.clear();
    resolvedYears.clear();
}

/**
 * Jahr des letzten Datenbezugs für den Quellenvermerk „© GeoBasis-DE / BKG (Jahr) CC BY 4.0“.
 * Bis zur Antwort und bei Fehlern das aktuelle Jahr.
 */
export function useBasemapDataYear(): number {
    const avmApiFullUrl = useSelector(selectAvmApiFullUrl);
    const [year, setYear] = useState<number>(
        () => (avmApiFullUrl ? resolvedYears.get(avmApiFullUrl) : undefined) ?? new Date().getFullYear(),
    );

    useEffect(() => {
        if (!avmApiFullUrl) return;
        let cancelled = false;
        void loadBasemapDataYear(avmApiFullUrl).then((loaded) => {
            if (!cancelled) setYear(loaded);
        });
        return () => {
            cancelled = true;
        };
    }, [avmApiFullUrl]);

    return year;
}
